import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { dataService } from '../services/dataService';
import { permitScope } from '../services/permitService';

// ─────────────────────────────────────────────────────────────────────────
//  EERSTE STAPPEN: de lege omgeving uitleggen.
//
//  Een verse beheerder landt na registratie in een omgeving zonder hallen,
//  zonder team en zonder werkplannen, en niemand vertelt hem wat de eerste
//  stap is. Precies op het verste punt van de reis sterft hij dan. Dit
//  blok staat op het startscherm zolang een van de drie stappen open
//  staat, en verdwijnt vanzelf zodra alles staat.
// ─────────────────────────────────────────────────────────────────────────

export default function EersteStappen({ currentUser, onNavigate }) {
    const data = useData() || {};
    const adminData = data.adminData;
    const [heeftWerkplan, setHeeftWerkplan] = useState(null); // null = nog niet bekeken

    const locatie = currentUser?.allowedLocations?.[0];
    const isBeheerder = currentUser?.role === 'admin';

    // Stap 1: staat er al iets van een fabrieksindeling?
    const locatieData = adminData?.locations?.[locatie];
    const heeftHierarchie = !!(locatieData?.hierarchy && Object.keys(locatieData.hierarchy).length > 0);

    // Stap 2: is er iemand naast de beheerder zelf?
    const teamleden = (adminData?.users || []).filter(u =>
        u.uid !== currentUser?.uid && (u.allowedLocations || []).includes(locatie));
    const heeftTeam = teamleden.length > 0;

    // Stap 3: bestaat er al een werkplan? Alleen opvragen zolang het blok
    // uberhaupt iets te melden heeft, en maar een keer.
    useEffect(() => {
        if (!isBeheerder || !locatie || heeftWerkplan !== null) return;
        if (heeftHierarchie && heeftTeam) { setHeeftWerkplan(true); return; }
        let actief = true;
        dataService.getAllPermits(permitScope(currentUser))
            .then(p => { if (actief) setHeeftWerkplan(p.length > 0); })
            .catch(() => { if (actief) setHeeftWerkplan(false); });
        return () => { actief = false; };
    }, [isBeheerder, locatie, heeftHierarchie, heeftTeam, heeftWerkplan, currentUser]);

    if (!isBeheerder || !locatie || !adminData) return null;

    const stappen = [
        {
            titel: 'Zet uw fabriek erin',
            uitleg: 'Maak uw eerste hal en lijn aan. Alles wat u daarna doet, hangt hieraan.',
            klaar: heeftHierarchie,
            knop: 'Naar de indeling',
            actie: () => onNavigate('admin_dashboard')
        },
        {
            titel: 'Maak accounts voor uw team',
            uitleg: 'Monteurs en werkverantwoordelijken krijgen elk hun eigen inlog en rol.',
            klaar: heeftTeam,
            knop: 'Naar gebruikersbeheer',
            actie: () => onNavigate('admin_dashboard')
        },
        {
            titel: 'Maak uw eerste werkplan',
            uitleg: 'Doorloop de wizard een keer zelf, dan weet u wat uw monteurs straks zien.',
            klaar: heeftWerkplan === true,
            knop: 'Start de wizard',
            actie: () => onNavigate('wizard', true)
        }
    ];

    // Alles klaar: niets meer te begeleiden, blok weg.
    if (stappen.every(s => s.klaar)) return null;

    return (
        <div className="card p-6 mb-8" style={{ borderLeft: '4px solid #0ea5e9' }}>
            <h3 className="text-lg font-bold mb-1">Uw omgeving staat, nu inrichten</h3>
            <p className="text-slate-400 text-sm mb-5">
                Drie stappen, in deze volgorde. Dit blok verdwijnt vanzelf zodra alles staat.
            </p>
            <div className="space-y-3">
                {stappen.map((s, i) => (
                    <div key={i} className="flex items-start gap-4 p-3 rounded-lg"
                        style={{ background: s.klaar ? 'rgba(16,185,129,0.08)' : 'rgba(148,163,184,0.06)' }}>
                        <span className="text-xl" aria-hidden="true">{s.klaar ? '✅' : `${i + 1}.`}</span>
                        <div className="flex-1">
                            <p className={`font-bold ${s.klaar ? 'text-slate-400 line-through' : ''}`}>{s.titel}</p>
                            {!s.klaar && <p className="text-sm text-slate-400">{s.uitleg}</p>}
                        </div>
                        {!s.klaar && (
                            <button onClick={s.actie} className="btn btn-primary btn-sm whitespace-nowrap">
                                {s.knop}
                            </button>
                        )}
                    </div>
                ))}
            </div>
            <p className="text-xs text-slate-500 mt-4">
                Een tweede vestiging erbij? Dat regelen wij voor u: mail naar
                {' '}<a href="mailto:info@raconsoft.nl" className="underline">info@raconsoft.nl</a>.
            </p>
        </div>
    );
}
